import { useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import {
  AUTH_TRACKING_ROWS,
  AUTH_TRACKING_STATS,
  AuthStatusTone,
  AuthTrackingRecord,
} from "./authTrackingData";
import AuthDetailPanel from "./components/AuthDetailPanel";
import "./AuthTracking.scss";

const StatusCell = ({
  label,
  tone,
}: {
  label: string;
  tone: AuthStatusTone;
}) => <span className={`at-status at-status--${tone}`}>{label}</span>;

const AuthTracking = () => {
  const [selectedAuth, setSelectedAuth] = useState<AuthTrackingRecord | null>(
    null,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [localPageSize, setLocalPageSize] = useState(10);
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [priorAuthNumber, setPriorAuthNumber] = useState("");
  const [appliedPriorAuthNumber, setAppliedPriorAuthNumber] = useState("");

  const filteredRows = useMemo(() => {
    const query = appliedPriorAuthNumber.trim().toLowerCase();
    if (!query) return AUTH_TRACKING_ROWS;
    return AUTH_TRACKING_ROWS.filter((row) =>
      row.priorAuthNumber.toLowerCase().includes(query),
    );
  }, [appliedPriorAuthNumber]);

  const totalRecords = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalRecords / localPageSize));

  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * localPageSize;
    return filteredRows.slice(start, start + localPageSize);
  }, [currentPage, localPageSize, filteredRows]);

  const handleSearch = () => {
    setAppliedPriorAuthNumber(priorAuthNumber);
    setCurrentPage(1);
  };

  const handleClear = () => {
    setFromDate(null);
    setToDate(null);
    setPriorAuthNumber("");
    setAppliedPriorAuthNumber("");
    setCurrentPage(1);
  };

  const handlePageChange = (newPageNumber: number) => {
    if (newPageNumber >= 1 && newPageNumber <= totalPages) {
      setCurrentPage(newPageNumber);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setLocalPageSize(newPageSize);
    setCurrentPage(1);
  };

  return (
    <div className="main-content auth-tracking-page">
      <div className="content-header d-flex justify-content-between align-items-center">
        <h1 className="page-header">Auth Tracking</h1>
      </div>

      <div className="card at-main-card">
        <div className="card-body">
          <div className="at-stats-row" aria-label="Auth tracking summary">
            {AUTH_TRACKING_STATS.map((stat) => (
              <article key={stat.id} className="at-stat-card">
                <span className="at-stat-icon" aria-hidden="true">
                  <i className={stat.icon} />
                </span>
                <div className="at-stat-copy">
                  <p className="at-stat-value">{stat.value}</p>
                  <h2 className="at-stat-title">{stat.title}</h2>
                </div>
              </article>
            ))}
          </div>

          <div className="card-form form-content mt-0 at-filter-form">
            <div className="row align-items-end">
              <div className="col-sm-6 col-md-4 col-xl-3 mb-4 mb-md-4 mb-xl-0">
                <label htmlFor="PriorAuthNumber" className="d-flex form-label">
                  Prior Auth Number
                </label>
                <div className="form-field">
                  <input
                    id="PriorAuthNumber"
                    type="text"
                    className="form-control h-48"
                    placeholder="Enter here"
                    value={priorAuthNumber}
                    onChange={(event) => setPriorAuthNumber(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        handleSearch();
                      }
                    }}
                    autoComplete="off"
                  />
                </div>
              </div>
              <div className="col-sm-6 col-md-4 col-xl-3 mb-4 mb-md-4 mb-xl-0">
                <label htmlFor="SubmittedFromDate" className="d-flex form-label">
                  From Date
                </label>
                <div className="form-field position-relative">
                  <DatePicker
                    id="SubmittedFromDate"
                    className={`form-control h-48`}
                    placeholderText="MM/DD/YYYY"
                    dateFormat="MM/dd/yyyy"
                    selected={fromDate}
                    onChange={(date) => {
                      setFromDate(date);
                    }}
                    selectsStart
                    startDate={fromDate}
                    endDate={toDate}
                    maxDate={toDate || undefined}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    portalId="root"
                  />
                </div>
              </div>
              <div className="col-sm-6 col-md-4 col-xl-3 mb-4 mb-md-4 mb-xl-0">
                <label htmlFor="SubmittedToDate" className="d-flex form-label">
                  To Date
                </label>
                <div className="form-field position-relative">
                  <DatePicker
                    id="SubmittedToDate"
                    className={`form-control h-48`}
                    placeholderText="MM/DD/YYYY"
                    dateFormat="MM/dd/yyyy"
                    selected={toDate}
                    onChange={(date) => {
                      setToDate(date);
                    }}
                    selectsEnd
                    startDate={fromDate}
                    endDate={toDate}
                    minDate={fromDate || undefined}
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    portalId="root"
                  />
                </div>
              </div>
              <div className="col-sm-12 col-md-auto mb-4 mb-md-4 mb-xl-0 at-filter-actions">
                <button
                  type="button"
                  className="btn btn-primary h-48 me-3"
                  onClick={handleSearch}
                >
                  Search
                </button>
                <button
                  type="button"
                  className="btn btn-border-primary h-48"
                  onClick={handleClear}
                >
                  Clear
                </button>
              </div>
            </div>
          </div>

          <div className="card-content">
            <div className="content-subheader d-flex align-items-center">
              <h2 className="page-subheader me-3">All received auths</h2>
              <span className="total-record">
                [ Total Auths: {totalRecords} ]
              </span>
            </div>
            <p className="at-section-hint">
              Select a prior auth number to view the audit trail for that
              request.
            </p>
            <div className="table-responsive">
              <table className="table caption-top">
                <thead>
                  <tr>
                    <th>EHR Company (Sender)</th>
                    <th className="at-prior-auth-col">Prior Auth Number</th>
                    <th>Payer</th>
                    <th>Submission</th>
                    <th>AuthStatus</th>
                    <th>Delivery</th>
                    <th>HTTP</th>
                    <th>Attempts</th>
                    <th>Submitted</th>
                    <th>Received Date</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="text-center">
                        No data found
                      </td>
                    </tr>
                  ) : (
                    pageRows.map((row) => (
                      <tr key={row.id}>
                        <td>
                          <span
                            className="at-ehr-company"
                            title={row.ehrOrganization}
                          >
                            {row.ehrOrganization}
                          </span>
                        </td>
                        <td className="at-prior-auth-col">
                          <span
                            className="at-claim-id cursor-pointer primary text-decoration-underline"
                            role="button"
                            tabIndex={0}
                            onClick={() => setSelectedAuth(row)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                setSelectedAuth(row);
                              }
                            }}
                          >
                            {row.priorAuthNumber}
                          </span>
                        </td>
                        <td>{row.payer}</td>
                        <td>
                          <StatusCell
                            label={row.submission}
                            tone={row.submissionTone}
                          />
                        </td>
                        <td>
                          <StatusCell
                            label={row.authStatus}
                            tone={row.authStatusTone}
                          />
                        </td>
                        <td>
                          <StatusCell
                            label={row.delivery}
                            tone={row.deliveryTone}
                          />
                        </td>
                        <td>{row.http}</td>
                        <td>{row.attempts}</td>
                        <td>
                          <div className="at-submitted-cell">
                            <div>{row.submittedDate}</div>
                            <div>{row.submittedTime}</div>
                          </div>
                        </td>
                        <td>
                          <div className="at-submitted-cell">
                            <div>{row.receivedDate}</div>
                            <div>{row.receivedTime}</div>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {pageRows.length > 0 && (
              <div className="d-flex justify-content-center">
                <nav
                  aria-label="Page navigation"
                  className="d-flex align-items-center"
                >
                  <span className="pagination-info me-2">
                    Page{" "}
                    <strong>
                      {currentPage} of {totalPages}
                    </strong>
                  </span>
                  <ul className="pagination d-flex justify-content-end mb-0">
                    <li className="page-item">
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                      >
                        <i className="fh_double_arrow_left"></i>
                      </button>
                    </li>
                    <li className="page-item">
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        <i className="fh_arrow_left_line"></i>
                      </button>
                    </li>
                    <li className="page-item">
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                      >
                        <i className="fh_arrow_right_line"></i>
                      </button>
                    </li>
                    <li className="page-item">
                      <button
                        type="button"
                        className="page-link"
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                      >
                        <i className="fh_double_arrow_right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
                <div>
                  <select
                    className="form-select form-control cursor-pointer custom-table-dropdown"
                    value={localPageSize}
                    onChange={(e) =>
                      handlePageSizeChange(Number(e.target.value))
                    }
                  >
                    {[10, 20, 30].map((pageSize) => (
                      <option key={pageSize} value={pageSize}>
                        Show {pageSize}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <AuthDetailPanel
        show={Boolean(selectedAuth)}
        auth={selectedAuth}
        onClose={() => setSelectedAuth(null)}
      />
    </div>
  );
};

export default AuthTracking;
